import { IncomingHttpHeaders } from 'http';
import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import inPreviewMode from '#utils/inPreviewMode';
import isSSR from '#utils/is-ssr';
import getRequestLocale from '#utils/getRequestLocale';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { onError } from '@apollo/client/link/error';
import { sha256 } from 'crypto-hash';
import getConfigVars from '#utils/get-config-vars';
import { getToken } from 'next-auth/jwt';
import { reportGraphQLErrors } from '#utils/dd';

const { GRAPHQL_SERVER_EXTERNAL } = getConfigVars({
  GRAPHQL_SERVER_EXTERNAL: '/api/graphql',
});

const getBaseOptions = () => {
  if (isSSR()) {
    // We don't want any cache to be stored server side
    return {
      persistedQueries: false,
      ssrMode: true,
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    };
  } else {
    // We immediately show results, but check in the background if any changes occurred, and eventually update the view
    return {
      ssrMode: false,
      query: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
    };
  }
};

// next.config.js serves every path as `public, s-maxage=...`, so a render that
// used the caller's token must opt out or the CDN will hand it to other users.
const preventSharedCaching = (context) => {
  if (context?.res && !context.res.headersSent) {
    context.res.setHeader('Cache-Control', 'private, no-cache');
  }
};

const getEnhancedFetch = (context, previewMode, headers) => {
  return async (url: RequestInfo, init: RequestInit) => {
    let authToken = null;

    if (isSSR()) {
      if (context?.req) {
        try {
          const session = await getToken({
            req: context.req,
            secret: process.env.NEXTAUTH_SECRET,
          });
          authToken = session?.accessToken || null;
        } catch (e) {
          // ignore token fetch failures on SSR gracefully
        }

        if (authToken) {
          preventSharedCaching(context);
        }
      }
    } else {
      const sessionUser = sessionStorage.getItem('user');
      const { token, expires } = (sessionUser && JSON.parse(sessionUser)) || {};
      authToken = expires && new Date(expires).getTime() > new Date().getTime() ? token : null;
    }

    const locale = getRequestLocale(context);

    const _headers = {
      'Accept-Language': locale,
      ...(authToken && {
        Authorization: `Bearer ${authToken}`,
      }),
      ...(previewMode && {
        'preview-mode': true,
      }),
      ...headers,
      ...(init.headers && init.headers),
    };

    const extraQueryParams =
      (String(url).includes('?') ? '&' : '?') +
      new URLSearchParams({
        locale,
        ...(authToken && { user: 'true' }),
      });

    return fetch(String(url) + extraQueryParams, {
      ...init,
      headers: _headers,
    }).then((response) => response);
  };
};

const createClient = (
  headers: IncomingHttpHeaders,
  context = null
): ApolloClient<NormalizedCacheObject> => {
  const previewMode = inPreviewMode(context?.resolvedUrl);

  const uri = isSSR()
    ? process.env.GRAPHQL_SERVER_INTERNAL
    : GRAPHQL_SERVER_EXTERNAL;

  const enhancedFetch = getEnhancedFetch(context, previewMode, headers);

  const httpLink = new HttpLink({
    uri,
    credentials: 'include',
    fetch: enhancedFetch,
  });

  const persistedHttpLink = createPersistedQueryLink({
    sha256,
    useGETForHashedQueries: true,
  }).concat(httpLink);

  // Datadog RUM only runs in the browser.
  const linkChain = isSSR()
    ? persistedHttpLink
    : onError(reportGraphQLErrors).concat(persistedHttpLink);

  const baseOptions = getBaseOptions();

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: linkChain,
    ...baseOptions,
  });
};

export default createClient;
